import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import FormFieldWrapper from '../../../../../common/components/FormFieldWrapper/FormFieldWrapper';
import PaneSection from '../../../../../common/components/PaneSection/PaneSection';
import { ProfilePictureEditor } from '../../../../../common/components/ProfilePictureEditor/ProfilePictureEditor';
import TextInput from '../../../../../common/components/TextInput';
import { usePersonasCRUDActions } from '@mm/state/actions/PersonasCRUDActions';
import { useUploadActions } from '@mm/state/actions/uploadActions';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { selectCurrentUser } from '@mm/state/selectors/userSelector';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import './LiveChatPersonaPaneSection.pcss';

export const LiveChatPersonaPaneSection = () => {
  const currentUser = useSelector(selectCurrentUser);
  const activeCompanyId = useSelector(selectActiveCompanyId);
  const [personaName, setPersonaName] = useState('');
  const [imageFile, setImageFile] = useState<File>();
  const [personaImageURL, setPersonaImageURL] = useState<string>();
  const { updateMe, busy, unbusy } = usePersonasCRUDActions();
  const usersCRUDActions = useUsersCRUDActions();
  const { uploadImage } = useUploadActions();
  const [reset, setReset] = useState(false);

  const defaultPersonaName =
    currentUser?.personaName ||
    `${currentUser?.firstName} ${currentUser?.lastName}`;
  const dirty = !!imageFile || personaName !== defaultPersonaName;

  useEffect(() => {
    if (currentUser) {
      setPersonaName(name => name || defaultPersonaName);
      setPersonaImageURL(
        url => url || currentUser.personaAssetUrl || currentUser.image,
      );
    }
    setReset(false);
  }, [currentUser, defaultPersonaName, reset]);
  const acceptImage = (file: File) => {
    setImageFile(file);
    try {
      const reader = new FileReader();
      reader.onload = e => setPersonaImageURL(e.target?.result as string);
      reader.readAsDataURL(file);
    } catch {}
  };

  const save = async () => {
    if (currentUser) {
      let personaAssetId: number | undefined;
      try {
        busy();
        if (imageFile) {
          const extension = imageFile.type.split('/').pop();
          if (extension) {
            personaAssetId = await uploadImage({
              content: imageFile,
              extension,
            });
          }
        }
        await updateMe(personaName, personaAssetId);
        await usersCRUDActions.retrieveAll(activeCompanyId);
      } finally {
        unbusy();
      }
      setReset(true);
      setImageFile(undefined);
    }
  };
  if (!currentUser) {
    return null;
  }

  return (
    <PaneSection
      title="Live Chat Persona"
      helpHref="https://mobilemonkey.com/help/article/di0r8zfm9k-agent-persona"
      classNameBody="live-chat-persona-pane-section__body"
    >
      <ProfilePictureEditor
        profilePictureUrl={personaImageURL}
        onChange={acceptImage}
      />
      <div className="live-chat-persona-pane-section__fields">
        <FormFieldWrapper label="Persona Name">
          <TextInput
            value={personaName}
            onChange={e => setPersonaName(e.target.value)}
            error={personaName.trim().length === 0}
          />
        </FormFieldWrapper>
        {dirty && (
          <PrimaryButton
            className="live-chat-persona-pane-section__save-button"
            disabled={!personaName.trim()}
            onClick={save}
          >
            Save
          </PrimaryButton>
        )}
      </div>
    </PaneSection>
  );
};
